import { useMemo, useState } from 'react'
import { ImageIcon, SmilePlus } from 'lucide-react'
import type { CoverImageMeta } from '@/components/features/cover-image/useCoverImage'
import type { UnsplashPhoto } from '@/lib/api/features/unsplash'
import { cn } from '@/lib/utils'
import { CoverImage } from '@/components/features/cover-image/CoverImage'
import { CoverImagePicker } from '@/components/features/cover-image/CoverImagePicker'
import { UnifiedIconPicker } from '@/components/app/pickers/unified-icon-picker'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useMembers } from '@/lib/hooks/useWorkspaceUsers'
import { memberAvatarSrc } from '@/lib/utils/memberAvatar'

const MAX_VISIBLE_CONTRIBUTORS = 5

function getInitials(name?: string | null, email?: string | null): string {
  if (name) return name.slice(0, 2).toUpperCase()
  if (email) return email.slice(0, 2).toUpperCase()
  return 'U'
}

interface DocumentHeaderProps {
  // Cover image
  coverDisplayUrl: string | null
  coverMeta: CoverImageMeta | null
  coverImageType: string | null | undefined
  hasCover: boolean
  isCoverUploading: boolean
  coverUploadProgress: number
  onCoverFromUnsplash: (photo: UnsplashPhoto) => void
  onCoverUpload: (file: File) => Promise<void>
  onCoverRemove: () => void
  onCoverPositionChange: (positionY: number) => void

  // Icon
  icon: string | null
  iconType: string | null
  iconColor?: string | null
  onIconChange: (value: { icon: string | null; iconType: string | null; iconColor?: string | null } | null) => void

  // Title
  title: string
  titlePlaceholder?: string
  onTitleChange: (title: string) => void
  onTitleBlur: () => void
  /** Put the cursor in the title on open, e.g. for a brand-new doc (design 32a). */
  autoFocusTitle?: boolean
  /** Enter in the title: typically moves on to the body. */
  onTitleEnter?: () => void
  maxTitleLength?: number

  // Contributors (auto-tracked editors of the document)
  contributorUserIds?: Array<string>

  // Content below header
  children?: React.ReactNode
}

export function DocumentHeader({
  coverDisplayUrl,
  coverMeta,
  coverImageType,
  hasCover,
  isCoverUploading,
  coverUploadProgress,
  onCoverFromUnsplash,
  onCoverUpload,
  onCoverRemove,
  onCoverPositionChange,
  icon,
  iconType,
  iconColor,
  onIconChange,
  title,
  titlePlaceholder = 'Untitled',
  onTitleChange,
  onTitleBlur,
  autoFocusTitle = false,
  onTitleEnter,
  maxTitleLength = 500,
  contributorUserIds,
  children,
}: DocumentHeaderProps) {
  const [isCoverPickerOpen, setIsCoverPickerOpen] = useState(false)
  const [isHoveringHeader, setIsHoveringHeader] = useState(false)
  const { users: members } = useMembers()

  const hasIcon = !!icon

  // Resolve contributor user IDs against the workspace member directory.
  // Members not yet synced locally are skipped silently — they reappear once members replicate.
  const contributors = useMemo(() => {
    if (!contributorUserIds || contributorUserIds.length === 0) return []
    const memberByUserId = new Map(members.map((m) => [m.userId, m]))
    return contributorUserIds
      .map((id) => memberByUserId.get(id))
      .filter((m): m is NonNullable<typeof m> => m != null)
  }, [contributorUserIds, members])

  const visibleContributors = contributors.slice(0, MAX_VISIBLE_CONTRIBUTORS)
  const hiddenCount = Math.max(0, contributors.length - MAX_VISIBLE_CONTRIBUTORS)

  return (
    <>
      <div
        className="group/doc-header"
        onMouseEnter={() => setIsHoveringHeader(true)}
        onMouseLeave={() => setIsHoveringHeader(false)}
      >
        {/* Cover Image */}
        <CoverImage
          displayUrl={coverDisplayUrl}
          meta={coverMeta}
          coverImageType={coverImageType}
          hasCover={hasCover}
          onChangeCover={() => setIsCoverPickerOpen(true)}
          onRemoveCover={onCoverRemove}
          onPositionChange={onCoverPositionChange}
        />

        {/* Content area with max-width */}
        <div className="mx-auto w-full max-w-3xl px-8 md:px-16">
          {/* Icon - overlaps cover image when present */}
          {hasIcon && (
            <div className={cn('relative', hasCover ? '-mt-8' : 'mt-6')}>
              <UnifiedIconPicker
                iconType={iconType}
                icon={icon}
                iconColor={iconColor}
                onValueChange={onIconChange}
              >
                <button
                  type="button"
                  aria-label="Change icon"
                  className="flex h-16 w-16 items-center justify-center rounded-lg bg-background shadow-sm border border-border/50 hover:bg-accent/50 transition-colors cursor-pointer"
                >
                  <ProjectIcon iconType={iconType} icon={icon} iconColor={iconColor} size={36} />
                </button>
              </UnifiedIconPicker>
            </div>
          )}

          {/* Hover action buttons (Notion-style) */}
          {(!hasIcon || !hasCover) && (
            <div className={cn(
              'flex items-center gap-1 mt-2 transition-opacity',
              !hasIcon && !hasCover && 'mt-6',
              isHoveringHeader ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}>
              {!hasIcon && (
                <button
                  type="button"
                  onClick={() => onIconChange({ icon: 'smile', iconType: 'lucide' })}
                  className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:bg-secondary/80 rounded-md transition-colors"
                >
                  <SmilePlus className="size-3.5" />
                  Add icon
                </button>
              )}
              {!hasCover && (
                <button
                  type="button"
                  onClick={() => setIsCoverPickerOpen(true)}
                  className="flex items-center gap-1.5 px-2 py-1 text-xs text-muted-foreground hover:bg-secondary/80 rounded-md transition-colors"
                >
                  <ImageIcon className="size-3.5" />
                  Add cover
                </button>
              )}
            </div>
          )}

          {/* Title */}
          <input
            value={title}
            onChange={(e) => onTitleChange(e.target.value.slice(0, maxTitleLength))}
            onBlur={onTitleBlur}
            autoFocus={autoFocusTitle}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || !onTitleEnter || e.nativeEvent.isComposing) return
              e.preventDefault()
              onTitleEnter()
            }}
            placeholder={titlePlaceholder}
            className={cn(
              'w-full border-none bg-transparent text-3xl md:text-4xl font-bold leading-tight tracking-tight text-foreground outline-none focus-visible:ring-0 placeholder:text-muted-foreground/40',
              hasIcon || hasCover ? 'mt-3' : 'mt-1',
            )}
          />

          {/* Contributor avatars */}
          {visibleContributors.length > 0 && (
            <div className="mt-3 flex items-center -space-x-1.5">
              {visibleContributors.map((member) => {
                const label = member.name || member.email || 'Contributor'
                const avatarSrc = memberAvatarSrc(member)
                return (
                  <Tooltip key={member.id}>
                    <TooltipTrigger asChild>
                      <Avatar className="size-6 rounded-full ring-2 ring-background">
                        {avatarSrc ? (
                          <AvatarImage
                            src={avatarSrc}
                            alt={member.name || member.email || ''}
                          />
                        ) : null}
                        <AvatarFallback className="rounded-full text-[10px]">
                          {getInitials(member.name, member.email)}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent>{label}</TooltipContent>
                  </Tooltip>
                )
              })}
              {hiddenCount > 0 && (
                <div className="size-6 rounded-full ring-2 ring-background bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground">
                  +{hiddenCount}
                </div>
              )}
            </div>
          )}

          {/* Content below title */}
          {children}
        </div>
      </div>

      {/* Cover Image Picker Dialog */}
      <CoverImagePicker
        open={isCoverPickerOpen}
        onOpenChange={setIsCoverPickerOpen}
        onSelectUnsplash={(photo) => {
          onCoverFromUnsplash(photo)
        }}
        onUploadCustom={onCoverUpload}
        isUploading={isCoverUploading}
        uploadProgress={coverUploadProgress}
      />
    </>
  )
}
